
import Vue from 'vue';
import { Data as ArticleData, Headlines as ArticleHeadlines } from '@root/modules/article/types/article';
import { DataWithHref, Data as HeadlinesData } from '@root/modules/headlines/types/headlines';
import getHeadlineWithHref from '@root/modules/headlines/utils/getHeadlineWithHref';
import ArticleHeadline from './ArticleHeadline.vue';
import Swiper from '@core/components/UI/swiper/Swiper.vue';
import getArticleUrl from '@root/modules/article/utils/buildArticleUrl';

interface Methods {
  addClickData: (headline: HeadlinesData, params: Record<string, any>) => Record<string, any>;
  getHeadline: (headline: HeadlinesData) => DataWithHref;
  getCxenseHeadline: (headline: HeadlinesData) => DataWithHref;
  toggleTab: (section: string) => void;
  swiperOptions: (container: string) => Record<string, any>;
}

interface Computed {
  headlineList: DataWithHref[];
  isCrArticle: boolean;
  crIndex: number | null;
  cxenseArticles: {
    id: string;
    clickUrl: string;
  }[];
  cxenseHeadlines: HeadlinesData[];
  mixHeadlines: HeadlinesData[];
  ptHeadlines: HeadlinesData[];
}

interface Props {
  article: ArticleData;
  headlines: ArticleHeadlines;
  limit: number;
  start: number;
  tabbed: boolean;
  recommendedLimit: number;
  recommendedStart: number;
  betweenArticles: boolean;
  dataSource: string;
}

interface Data {
  domains: string | string[];
  locale: string;
  cxenseHeadlinesLimit: number;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleHeadline,
    Swiper,
  },
  props: {
    article: {
      type: Object,
      required: false,
      default: null,
    },
    headlines: {
      type: Array,
      required: false,
      default: null,
    },
    limit: {
      type: Number,
      required: false,
      default: 6,
    },
    start: {
      type: Number,
      required: false,
      default: 0,
    },
    tabbed: {
      type: Boolean,
      required: false,
      default: false,
    },
    recommendedLimit: {
      type: Number,
      required: false,
      default: 6,
    },
    recommendedStart: {
      type: Number,
      required: false,
      default: 0,
    },
    betweenArticles: {
      type: Boolean,
      required: false,
      default: false,
    },
    dataSource: {
      type: String,
      required: false,
      default: 'cxense',
    },
  },
  data() {
    const { domain, locale } = this.$channelConfig('settings');
    return {
      domains: domain,
      locale,
      cxenseHeadlinesLimit: 20,
    };
  },
  computed: {
    headlineList() {
      const headlines = (this.headlines && this.headlines.slice(this.start, this.limit).map((headline) => this.getHeadline(headline))) || [];

      return headlines;
    },
    isCrArticle() {
      const articles = this.$store.getters['article/getCrArticles'];

      return articles.find((article: number) => article === this.article.id);
    },
    crIndex() {
      if (this.isCrArticle) {
        const id = this.article.id;
        const crArticles = this.$store.state.article.crArticles;
        return crArticles.indexOf(id) + 1;
      }

      return null;
    },
    cxenseArticles() {
      return this.$store.getters['article/getArticleCxenseArticles'] || [];
    },
    cxenseHeadlines() {
      let cxenseHeadlines = this.$store.getters['article/getArticleCxenseHeadlines'] || [];
      if (cxenseHeadlines.length) {
        let start =
          this.isCrArticle && this.crIndex ? this.recommendedStart * this.crIndex + this.recommendedStart + this.recommendedLimit : this.recommendedStart;
        let end = start + this.recommendedLimit;

        if (start > cxenseHeadlines.length) {
          start = this.cxenseArticles.length - this.recommendedLimit;
        }

        if (end > cxenseHeadlines.length) {
          end = cxenseHeadlines.length;
        }

        cxenseHeadlines = cxenseHeadlines.slice(start, end);
      }

      return cxenseHeadlines;
    },
    mixHeadlines() {
      const mixHeadlines: HeadlinesData[] = [];

      const topHeadlinesLimit = 7;
      let topHeadlinesStart = 0;
      let topHeadlinesEnd = topHeadlinesStart + topHeadlinesLimit;
      let topHeadlines = this.$store.getters['article/getArticleTopHeadlines'] || [];

      const ptHeadlinesLimit = 3;
      let ptHeadlinesStart = 0;
      let ptHeadlinesEnd = ptHeadlinesStart + ptHeadlinesLimit;
      let ptHeadlines = this.ptHeadlines || [];

      if (topHeadlines.length) {
        if (this.isCrArticle && this.crIndex) {
          topHeadlinesStart = this.crIndex * topHeadlinesLimit;
          if (topHeadlinesStart + topHeadlinesLimit > topHeadlines.length) {
            topHeadlinesStart = topHeadlines.length - topHeadlinesLimit;
          }

          topHeadlinesEnd = topHeadlinesStart + topHeadlinesLimit;
          if (topHeadlinesEnd > topHeadlines.length) {
            topHeadlinesEnd = topHeadlines.length;
          }

          ptHeadlinesStart = this.crIndex * ptHeadlinesLimit;
          if (ptHeadlinesStart + ptHeadlinesLimit > ptHeadlines.length) {
            ptHeadlinesStart = ptHeadlines.length - ptHeadlinesLimit;
          }

          ptHeadlinesEnd = ptHeadlinesStart + ptHeadlinesLimit;
          if (ptHeadlinesEnd > ptHeadlines.length) {
            ptHeadlinesEnd = ptHeadlines.length;
          }
        }

        topHeadlines = topHeadlines.length ? topHeadlines.slice(topHeadlinesStart, topHeadlinesEnd) : [];
        ptHeadlines = ptHeadlines.length ? ptHeadlines.slice(ptHeadlinesStart, ptHeadlinesEnd) : [];

        mixHeadlines[0] = topHeadlines[0];
        mixHeadlines[1] = topHeadlines[1];
        mixHeadlines[2] = ptHeadlines[0] || topHeadlines[2];
        mixHeadlines[3] = ptHeadlines[0] ? topHeadlines[2] : topHeadlines[3];
        mixHeadlines[4] = ptHeadlines[0] ? topHeadlines[3] : topHeadlines[4];

        if (ptHeadlines[1] || topHeadlines[5]) {
          mixHeadlines[5] = ptHeadlines[1] || topHeadlines[5];
        }

        if (ptHeadlines[2] || topHeadlines[6]) {
          mixHeadlines[6] = ptHeadlines[2] || topHeadlines[6];
        }
      }

      return mixHeadlines;
    },
    ptHeadlines() {
      return this.$store.getters['article/getArticlePtHeadlines'] || [];
    },
  },
  methods: {
    addClickData(headline: HeadlinesData, params: Record<string, any>) {
      const clickData: Record<string, any> = {};

      clickData.articleLayout = 'b';
      clickData.listType = params.listType;
      clickData.listPosition = params.listPosition;
      clickData.listArticleId = headline.id;
      clickData.listArticleUrl = getArticleUrl({ article: headline, domain: headline.primaryCategory.channel.url });
      clickData.listArticlePosition = params.listIndex + 1;

      return { ...headline, clickData };
    },
    getHeadline(headline) {
      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
      return getHeadlineWithHref({ headline, domain: this.domains, fakeDomainEnabled });
    },
    getCxenseHeadline(headline) {
      const cxenseArticle = this.cxenseArticles.find((article) => article.id === String(headline.id));
      return cxenseArticle ? { ...headline, href: cxenseArticle?.clickUrl } : this.getHeadline(headline);
    },
    toggleTab(section) {
      const id = this.$props.article.id;
      const container = window.document.querySelector(`[data-headlines-section="${id}"]`) as HTMLElement;
      const targetNavigationItem = container.querySelector(`.C-article-headlines__tabs-navigation--item-${section}`) as HTMLElement;
      const targetContainer = container.querySelector(`.C-article-headlines__tabs-content--tab-${section}`) as HTMLElement;
      const targetContainerIsActive = targetContainer.classList.contains('C-article-headlines__tabs-content--tab-active');

      const navigationItems = container.querySelectorAll('.C-article-headlines__tabs-navigation--item');
      navigationItems.forEach((element) => {
        const navigationItem = element as HTMLElement;
        navigationItem.classList.remove('C-article-headlines__tabs-navigation--item-active');
      });

      const tabs = container.querySelectorAll('.C-article-headlines__tabs-content--tab');
      tabs.forEach((element) => {
        const tab = element as HTMLElement;
        tab.classList.remove('C-article-headlines__tabs-content--tab-active');
      });

      if (!targetContainerIsActive) {
        targetNavigationItem.classList.toggle('C-article-headlines__tabs-navigation--item-active');
        targetContainer.classList.toggle('C-article-headlines__tabs-content--tab-active');
      }
    },
    swiperOptions(container: string) {
      const swiperOptions = {
        spaceBetween: 12,
        initialSlide: 0,
        loop: false,
        autoplay: false,
        slidesPerView: 1.2,
        slidesPerGroup: 1,
        pagination: {
          enabled: false,
        },
        navigation: {
          nextEl: `.${container} .swiper-button-next`,
          prevEl: `.${container} .swiper-button-prev`,
          enabled: false,
        },
        scrollbar: {
          enabled: false,
        },
        breakpoints: {
          768: {
            freeMode: false,
            slidesPerView: 3,
            slidesPerGroup: 3,
            loop: false,
            navigation: {
              enabled: true,
            },
          },
        },
      };

      return swiperOptions;
    },
  },
});
